/**
 * GridEditor — Property Editor for the "grid" control type
 *
 * 6 sections:
 *   1. Grid Setup      — title, gridType, idField, emptyMessage, editFormID
 *   2. CSS             — tableClass, altRowClass, bulkEditEnabled
 *   3. Columns         — visual column builder + JSON fallback
 *   4. New Entity      — JSON editor for newEntityTemplate
 *   5. Buttons         — visual button builder
 *   6. Features        — reorder, pagination, pageSize, export/import formats
 *
 * Data binding is configured via the unified "Data Binding" section in PropertiesTab
 * (control.binding — source / path / expression).
 *
 * SRP: this file is responsible ONLY for rendering the property panel.
 * No business logic; all grid types live in player-components-react/GridControl/grid.types.ts.
 */

import React, { useCallback, useMemo, useState } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import { TextPropertyEditor } from '../BaseEditors/TextPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';
import { JSONPropertyEditor } from '../BaseEditors/JSONPropertyEditor';
import FormLookupSelect from '../../FormContainers/FormLookupSelect';
import './GridEditor.css';

// ─── Local type mirrors (keep designer independent of player package) ─────────

type ColType = 'text' | 'number' | 'date' | 'select' | 'toggle' | 'computed' | 'selector' | 'css-class' | 'link';
type GridType = 'table' | 'card' | 'list';
/** Mirrors player-components-react's grid.types.ts `GridViewMode` — see that file's doc
 *  comment. Distinct from the legacy, never-wired-up `GridType`/`gridType` above. */
type GridViewMode = 'grid' | 'cards' | 'gallery';
/** Mirrors player-components-react's grid.types.ts `CardTemplateConfig`. */
interface CardTemplateConfig {
  titleField: string;
  imageField?: string;
  subtitleField?: string;
  descriptionField?: string;
  metaFields?: string[];
}
type ButtonAction = 'add' | 'deleteSelected' | 'editSelected' | 'addQuickItems';

interface SelectOption { value: string; label: string }
interface ColumnValidation { minLength?: number; maxLength?: number; min?: number; max?: number; message?: string }
interface GridColumn {
  key: string;
  /** Alias for key — raw JSON configs commonly use "id" instead of "key" (normalised to key at
   *  render time by player-components-react's GridControl; this editor reads the raw,
   *  un-normalised config, so field pickers here must fall back to `id` themselves). */
  id?: string;
  label: string; type?: ColType;
  width?: number; required?: boolean; placeholder?: string; default?: unknown;
  columnCss?: string; cellClassExpr?: string;
  min?: number; max?: number; step?: number;
  options?: SelectOption[]; selectorType?: 'checkbox' | 'radio';
  compute?: string; renderer?: string;
  validation?: ColumnValidation;
  dataBinding?: { fieldPath?: string };
  link?: LinkColumnConfig;
  /** Pins this column via `position: sticky` while the rest of the grid scrolls horizontally
   * underneath it. See `player-components-react`'s `grid.types.ts` `GridColumn.freezePosition`
   * doc comment for the full mechanism (contiguous leading/trailing run only). */
  freezePosition?: 'left' | 'right' | 'none';
}
interface GridButton {
  label: string; buttonAction: ButtonAction; icon?: string; emoji?: string;
  requiresSelection?: boolean; selectionLimit?: number; items?: Record<string, unknown>[];
}
interface GridConfig {
  title?: string; gridType?: GridType; idField?: string;
  pageSize?: number; emptyMessage?: string;
  editFormID?: number | null; bulkEditEnabled?: boolean;
  tableClass?: string; altRowClass?: string;
  columns?: GridColumn[]; newEntityTemplate?: Record<string, unknown>;
  buttons?: GridButton[];
  features?: { reorder?: boolean; pagination?: boolean; exportFormats?: string[]; importFormats?: string[] };
  /** Task 19 — result-rendering mode. Unset/'grid' is the existing table, unchanged. */
  viewMode?: GridViewMode;
  /** Field-mapping template for 'cards'/'gallery' rendering — see CardTemplateConfig. */
  cardTemplate?: CardTemplateConfig;
}

type LinkActionLeaf =
  | { kind: 'form'; formId: number; target?: string; mode?: 'view' | 'edit' }
  | { kind: 'url'; urlTemplate: string; target?: string }
  | { kind: 'expression'; expression: string }
  | { kind: 'delete'; endpointTemplate: string; method?: 'DELETE' | 'POST' | 'PUT'; confirmMessage?: string };

type LinkAction =
  | LinkActionLeaf
  | {
      kind: 'matrix';
      rules: Array<{ label?: string; condition: string; action: LinkActionLeaf }>;
      fallback: LinkActionLeaf;
    };

interface LinkColumnConfig {
  label?: string;
  action?: LinkAction;
  showAs?: 'button' | 'link';
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  icon?: string;
  visible?: string;
  /** Role names required to use this action; the button renders disabled (not hidden) otherwise. */
  requiredRoles?: string[];
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

const storageKey = (title: string) => `atlas-grid-editor-section:${title}`;

const Section: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey(title));
      return stored !== null ? stored === 'true' : defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  const toggle = () =>
    setOpen((o) => {
      const next = !o;
      try { localStorage.setItem(storageKey(title), String(next)); } catch { /* quota/private */ }
      return next;
    });

  return (
    <div className="af-property-editor__section">
      <button
        type="button"
        className={`af-property-editor__section-title${open ? ' af-property-editor__section-title--open' : ''}`}
        onClick={toggle}
      >
        <span className="af-property-editor__section-chevron">{open ? '▾' : '▸'}</span>
        {title}
      </button>
      {open && <div className="af-property-editor__section-body">{children}</div>}
    </div>
  );
};

// ─── Column row editor ────────────────────────────────────────────────────────

const COL_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'toggle', label: 'Toggle' },
  { value: 'computed', label: 'Computed' },
  { value: 'selector', label: 'Selector' },
  { value: 'css-class', label: 'CSS Class' },
  { value: 'link', label: 'Link / Action' },
];

interface ColumnRowProps {
  col: GridColumn;
  index: number;
  onUpdate: (index: number, updated: GridColumn) => void;
  onDelete: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
}

const ColumnRow: React.FC<ColumnRowProps> = ({
  col, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast,
}) => {
  const [expanded, setExpanded] = useState(false);

  const set = <K extends keyof GridColumn>(key: K, val: GridColumn[K]) =>
    onUpdate(index, { ...col, [key]: val });

  return (
    <div className="af-grid-editor__col-row">
      <div className="af-grid-editor__col-summary">
        <button
          type="button"
          className="af-grid-editor__col-expand"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? '▾' : '▸'} {col.label || col.key || `Column ${index + 1}`}
          {col.type && <span className="af-grid-editor__col-type-badge">{col.type}</span>}
        </button>
        <div className="af-grid-editor__col-actions">
          <button type="button" disabled={isFirst} onClick={() => onMoveUp(index)} title="Move up">↑</button>
          <button type="button" disabled={isLast} onClick={() => onMoveDown(index)} title="Move down">↓</button>
          <button type="button" onClick={() => onDelete(index)} title="Delete column" className="af-grid-editor__col-delete">✕</button>
        </div>
      </div>

      {expanded && (
        <div className="af-grid-editor__col-detail">
          <div className="af-grid-editor__col-row-fields">
            <TextPropertyEditor label="Key" value={col.key} onChange={(v) => set('key', v)} placeholder="fieldKey" />
            <TextPropertyEditor label="Label" value={col.label} onChange={(v) => set('label', v)} placeholder="Column label" />
          </div>
          <div className="af-grid-editor__col-row-fields">
            <SelectPropertyEditor
              label="Type"
              value={col.type ?? 'text'}
              onChange={(v) => set('type', v as ColType)}
              options={COL_TYPE_OPTIONS}
            />
            <NumberPropertyEditor label="Width (px)" value={col.width ?? 120} onChange={(v) => set('width', v)} min={20} max={600} step={10} />
          </div>
          <div className="af-grid-editor__col-row-fields">
            <CheckboxPropertyEditor label="Required" value={col.required ?? false} onChange={(v) => set('required', v)} />
            <TextPropertyEditor label="Placeholder" value={col.placeholder ?? ''} onChange={(v) => set('placeholder', v)} />
          </div>
          <SelectPropertyEditor
            label="Freeze column"
            value={col.freezePosition ?? 'none'}
            onChange={(v) => set('freezePosition', (v === 'none' ? undefined : v) as GridColumn['freezePosition'])}
            options={[
              { value: 'none', label: 'Not frozen' },
              { value: 'left', label: 'Frozen — pin to left edge' },
              { value: 'right', label: 'Frozen — pin to right edge' },
            ]}
            helpText="Pins this column so it stays visible while the rest of the grid scrolls horizontally. Only a contiguous leading run of left-frozen columns (and, symmetrically, trailing run of right-frozen columns) is supported."
          />
          <TextPropertyEditor label="Column CSS class (columnCss)" value={col.columnCss ?? ''} onChange={(v) => set('columnCss', v)} helpText="Static CSS class applied to every <td> in this column" />
          <TextPropertyEditor label="Cell class expression (cellClassExpr)" value={col.cellClassExpr ?? ''} onChange={(v) => set('cellClassExpr', v)} helpText="JS expression: (value, row) => CSS class string" />
          <TextPropertyEditor label="Data binding path" value={col.dataBinding?.fieldPath ?? ''} onChange={(v) => set('dataBinding', { ...col.dataBinding, fieldPath: v })} helpText="Dot-notation path into nested row object (e.g. address.city)" />

          {col.type === 'number' && (
            <div className="af-grid-editor__col-row-fields">
              <NumberPropertyEditor label="Min" value={col.min ?? 0} onChange={(v) => set('min', v)} />
              <NumberPropertyEditor label="Max" value={col.max ?? 100} onChange={(v) => set('max', v)} />
              <NumberPropertyEditor label="Step" value={col.step ?? 1} onChange={(v) => set('step', v)} />
            </div>
          )}

          {col.type === 'selector' && (
            <SelectPropertyEditor
              label="Selector type"
              value={col.selectorType ?? 'checkbox'}
              onChange={(v) => set('selectorType', v as 'checkbox' | 'radio')}
              options={[{ value: 'checkbox', label: 'Checkbox' }, { value: 'radio', label: 'Radio' }]}
            />
          )}

          {col.type === 'computed' && (
            <>
              <TextPropertyEditor label="Compute expression" value={col.compute ?? ''} onChange={(v) => set('compute', v)} helpText="JS expression with `row` variable, e.g. row.a + row.b" multiline rows={2} />
              <TextPropertyEditor label="Renderer" value={col.renderer ?? ''} onChange={(v) => set('renderer', v)} helpText="Named renderer (e.g. statusBadge)" />
            </>
          )}

          {col.type === 'select' && (
            <JSONPropertyEditor
              label="Options (JSON)"
              value={JSON.stringify(col.options ?? [], null, 2)}
              onChange={(v) => {
                try { set('options', JSON.parse(v)); } catch { /* keep previous */ }
              }}
              rows={6}
              helpText='Array of {value, label} objects'
            />
          )}

          {col.type === 'link' && (
            <LinkColumnEditor
              link={col.link ?? {}}
              onChange={(updated) => set('link', updated)}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Link column editor ───────────────────────────────────────────────────────

const LINK_VARIANT_OPTIONS = [
  { value: 'secondary', label: 'Secondary' },
  { value: 'primary', label: 'Primary' },
  { value: 'ghost', label: 'Ghost' },
  { value: 'danger', label: 'Danger' },
];

const LINK_SHOW_AS_OPTIONS = [
  { value: 'button', label: 'Button' },
  { value: 'link', label: 'Link text' },
];

const LINK_ACTION_KINDS = [
  { value: 'form', label: 'Form' },
  { value: 'url', label: 'URL' },
  { value: 'delete', label: 'Delete request' },
  { value: 'expression', label: 'Expression' },
  { value: 'matrix', label: 'Matrix' },
];

const LEAF_ACTION_KINDS = [
  { value: 'form', label: 'Form' },
  { value: 'url', label: 'URL' },
  { value: 'delete', label: 'Delete request' },
  { value: 'expression', label: 'Expression' },
];

const LINK_FORM_MODE_OPTIONS = [
  { value: 'edit', label: 'Edit (interactive, Save enabled)' },
  { value: 'view', label: 'View (read-only)' },
];

const LINK_DELETE_METHOD_OPTIONS = [
  { value: 'DELETE', label: 'DELETE' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
];

interface LeafActionEditorProps {
  action: LinkActionLeaf;
  onChange: (action: LinkActionLeaf) => void;
}

const LeafActionEditor: React.FC<LeafActionEditorProps> = ({ action, onChange }) => {
  const handleKindChange = (newKind: string) => {
    if (newKind === 'form') onChange({ kind: 'form', formId: 0, target: 'modal', mode: 'edit' });
    else if (newKind === 'url') onChange({ kind: 'url', urlTemplate: '', target: '_blank' });
    else if (newKind === 'delete') onChange({ kind: 'delete', endpointTemplate: '', method: 'DELETE' });
    else onChange({ kind: 'expression', expression: '' });
  };

  return (
    <div className="af-grid-editor__leaf-action">
      <SelectPropertyEditor
        label="Action type"
        value={action.kind}
        onChange={handleKindChange}
        options={LEAF_ACTION_KINDS}
      />
      {action.kind === 'form' && (
        <>
          <FormLookupSelect
            label="Form ID"
            value={action.formId ?? null}
            onChange={(v) => onChange({ ...action, formId: v !== null ? Number(v) : 0 })}
            placeholder="Select a form..."
          />
          <SelectPropertyEditor
            label="Open in"
            value={action.target ?? 'modal'}
            onChange={(v) => onChange({ ...action, target: v as 'modal' | 'page' | 'tab' })}
            options={[
              { value: 'modal', label: 'Modal (overlay)' },
              { value: 'page', label: 'Page (navigate)' },
              { value: 'tab', label: 'New tab' },
            ]}
          />
          <SelectPropertyEditor
            label="Form mode"
            value={action.mode ?? 'edit'}
            onChange={(v) => onChange({ ...action, mode: v as 'view' | 'edit' })}
            options={LINK_FORM_MODE_OPTIONS}
            helpText="'View' opens the same form read-only (no Save) — use it to reuse one form for both a View and an Edit row action."
          />
        </>
      )}
      {action.kind === 'url' && (
        <>
          <TextPropertyEditor
            label="URL template"
            value={action.urlTemplate ?? ''}
            onChange={(v) => onChange({ ...action, urlTemplate: v })}
            helpText="Supports {{row.fieldName}} interpolation"
          />
          <SelectPropertyEditor
            label="Target"
            value={action.target ?? '_self'}
            onChange={(v) => onChange({ ...action, target: v as '_blank' | '_self' })}
            options={[
              { value: '_self', label: 'Same tab' },
              { value: '_blank', label: 'New tab' },
            ]}
          />
        </>
      )}
      {action.kind === 'delete' && (
        <>
          <TextPropertyEditor
            label="Endpoint"
            value={action.endpointTemplate ?? ''}
            onChange={(v) => onChange({ ...action, endpointTemplate: v })}
            helpText="Supports {{row.fieldName}} interpolation, e.g. /api/v1/documents/{{row.documentID}}"
          />
          <SelectPropertyEditor
            label="Method"
            value={action.method ?? 'DELETE'}
            onChange={(v) => onChange({ ...action, method: v as 'DELETE' | 'POST' | 'PUT' })}
            options={LINK_DELETE_METHOD_OPTIONS}
          />
          <TextPropertyEditor
            label="Confirm message"
            value={action.confirmMessage ?? ''}
            onChange={(v) => onChange({ ...action, confirmMessage: v || undefined })}
            helpText="Shown in a confirm() prompt before the request fires. Supports {{row.fieldName}} interpolation."
          />
        </>
      )}
      {action.kind === 'expression' && (
        <TextPropertyEditor
          label="Expression"
          value={action.expression ?? ''}
          onChange={(v) => onChange({ ...action, expression: v })}
          helpText="JS with row variable. Return { formId: N }, { url: '...' }, or null."
          multiline
          rows={2}
        />
      )}
    </div>
  );
};

interface MatrixRuleRowProps {
  rule: { label?: string; condition: string; action: LinkActionLeaf };
  index: number;
  onUpdate: (rule: { label?: string; condition: string; action: LinkActionLeaf }) => void;
  onDelete: () => void;
}

const MatrixRuleRow: React.FC<MatrixRuleRowProps> = ({ rule, index, onUpdate, onDelete }) => (
  <div className="af-grid-editor__matrix-rule">
    <div className="af-grid-editor__matrix-rule-header">
      <span className="af-grid-editor__matrix-rule-num">Rule {index + 1}</span>
      <button type="button" onClick={onDelete} className="af-grid-editor__col-delete" title="Delete rule">✕</button>
    </div>
    <TextPropertyEditor
      label="Label (optional)"
      value={rule.label ?? ''}
      onChange={(v) => onUpdate({ ...rule, label: v || undefined })}
    />
    <TextPropertyEditor
      label="Condition"
      value={rule.condition}
      onChange={(v) => onUpdate({ ...rule, condition: v })}
      helpText="JS expression with row variable. e.g. row.type === 'invoice'"
    />
    <LeafActionEditor
      action={rule.action}
      onChange={(action) => onUpdate({ ...rule, action })}
    />
  </div>
);

interface LinkColumnEditorProps {
  link: LinkColumnConfig;
  onChange: (updated: LinkColumnConfig) => void;
}

const LinkColumnEditor: React.FC<LinkColumnEditorProps> = ({ link, onChange }) => {
  const action = link.action;
  const kind = action?.kind ?? 'form';

  const setLink = <K extends keyof LinkColumnConfig>(key: K, val: LinkColumnConfig[K]) =>
    onChange({ ...link, [key]: val });

  const setAction = (newAction: LinkAction) => setLink('action', newAction);

  const handleKindChange = (newKind: string) => {
    if (newKind === 'form') setAction({ kind: 'form', formId: 0, target: 'modal', mode: 'edit' });
    else if (newKind === 'url') setAction({ kind: 'url', urlTemplate: '', target: '_blank' });
    else if (newKind === 'delete') setAction({ kind: 'delete', endpointTemplate: '', method: 'DELETE' });
    else if (newKind === 'expression') setAction({ kind: 'expression', expression: '' });
    else setAction({ kind: 'matrix', rules: [], fallback: { kind: 'form', formId: 0, target: 'modal' } });
  };

  const matrixAction = action?.kind === 'matrix' ? action : null;

  return (
    <div className="af-grid-editor__link-config">
      <TextPropertyEditor
        label="Label"
        value={link.label ?? ''}
        onChange={(v) => setLink('label', v || undefined)}
        helpText="Button/link text. Supports {{row.fieldName}} interpolation."
      />
      <div className="af-grid-editor__col-row-fields">
        <SelectPropertyEditor
          label="Show as"
          value={link.showAs ?? 'button'}
          onChange={(v) => setLink('showAs', v as 'button' | 'link')}
          options={LINK_SHOW_AS_OPTIONS}
        />
        <SelectPropertyEditor
          label="Variant"
          value={link.variant ?? 'secondary'}
          onChange={(v) => setLink('variant', v as LinkColumnConfig['variant'])}
          options={LINK_VARIANT_OPTIONS}
        />
      </div>
      <TextPropertyEditor
        label="Icon"
        value={link.icon ?? ''}
        onChange={(v) => setLink('icon', v || undefined)}
        helpText="CSS icon class, e.g. pencil, external-link"
      />
      <TextPropertyEditor
        label="Visible (expression)"
        value={link.visible ?? ''}
        onChange={(v) => setLink('visible', v || undefined)}
        helpText="JS with row variable — link hidden when falsy. e.g. row.status !== 'archived'"
      />
      <TextPropertyEditor
        label="Required roles"
        value={(link.requiredRoles ?? []).join(', ')}
        onChange={(v) => {
          const roles = v.split(',').map((r) => r.trim()).filter(Boolean);
          setLink('requiredRoles', roles.length > 0 ? roles : undefined);
        }}
        helpText="Comma-separated role names (e.g. Admin, TenantSuperAdmin). Leave blank to allow any signed-in user. The button renders disabled — never hidden — for a user without one of these roles."
      />

      <div className="af-property-editor__group">
        <span className="af-property-editor__label">Action</span>
        <div className="af-grid-editor__link-kind-bar">
          {LINK_ACTION_KINDS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`af-grid-editor__link-kind-btn${kind === opt.value ? ' af-grid-editor__link-kind-btn--active' : ''}`}
              onClick={() => handleKindChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {(!action || action.kind === 'form') && (
        <>
          <FormLookupSelect
            label="Form ID"
            value={action?.kind === 'form' ? action.formId : null}
            onChange={(v) => setAction({ kind: 'form', formId: v !== null ? Number(v) : 0, target: action?.kind === 'form' ? action.target : undefined, mode: action?.kind === 'form' ? action.mode : 'edit' })}
            placeholder="Select a form..."
          />
          <SelectPropertyEditor
            label="Open in"
            value={(action?.kind === 'form' ? action.target : undefined) ?? 'modal'}
            onChange={(v) => setAction({ kind: 'form', formId: action?.kind === 'form' ? action.formId : 0, target: v as 'modal' | 'page' | 'tab', mode: action?.kind === 'form' ? action.mode : 'edit' })}
            options={[
              { value: 'modal', label: 'Modal (overlay)' },
              { value: 'page', label: 'Page (navigate)' },
              { value: 'tab', label: 'New tab' },
            ]}
          />
          <SelectPropertyEditor
            label="Form mode"
            value={(action?.kind === 'form' ? action.mode : undefined) ?? 'edit'}
            onChange={(v) => setAction({ kind: 'form', formId: action?.kind === 'form' ? action.formId : 0, target: action?.kind === 'form' ? action.target : undefined, mode: v as 'view' | 'edit' })}
            options={LINK_FORM_MODE_OPTIONS}
            helpText="'View' opens the same form read-only (no Save) — use it to reuse one form for both a View and an Edit row action."
          />
        </>
      )}

      {action?.kind === 'url' && (
        <>
          <TextPropertyEditor
            label="URL template"
            value={action.urlTemplate}
            onChange={(v) => setAction({ ...action, urlTemplate: v })}
            helpText="Supports {{row.fieldName}} interpolation, e.g. /records/{{row.id}}"
          />
          <SelectPropertyEditor
            label="Target"
            value={action.target ?? '_self'}
            onChange={(v) => setAction({ ...action, target: v as '_blank' | '_self' })}
            options={[
              { value: '_self', label: 'Same tab' },
              { value: '_blank', label: 'New tab' },
            ]}
          />
        </>
      )}

      {action?.kind === 'delete' && (
        <>
          <TextPropertyEditor
            label="Endpoint"
            value={action.endpointTemplate}
            onChange={(v) => setAction({ ...action, endpointTemplate: v })}
            helpText="Supports {{row.fieldName}} interpolation, e.g. /api/v1/documents/{{row.documentID}}. Entirely schema-configurable — not tied to any one entity type."
          />
          <SelectPropertyEditor
            label="Method"
            value={action.method ?? 'DELETE'}
            onChange={(v) => setAction({ ...action, method: v as 'DELETE' | 'POST' | 'PUT' })}
            options={LINK_DELETE_METHOD_OPTIONS}
          />
          <TextPropertyEditor
            label="Confirm message"
            value={action.confirmMessage ?? ''}
            onChange={(v) => setAction({ ...action, confirmMessage: v || undefined })}
            helpText="Shown in a confirm() prompt before the request fires. Supports {{row.fieldName}} interpolation."
          />
        </>
      )}

      {action?.kind === 'expression' && (
        <TextPropertyEditor
          label="Expression"
          value={action.expression}
          onChange={(v) => setAction({ ...action, expression: v })}
          helpText="JS with row variable. Return { formId: N }, { url: '...' }, or null."
          multiline
          rows={3}
        />
      )}

      {matrixAction && (
        <>
          <p className="af-property-editor__help" style={{ marginBottom: 6 }}>
            Rules evaluated top-to-bottom. First match wins. Fallback fires when no rule matches.
          </p>
          {matrixAction.rules.map((rule, i) => (
            <MatrixRuleRow
              key={i}
              rule={rule}
              index={i}
              onUpdate={(updated) => {
                const rules = [...matrixAction.rules];
                rules[i] = updated;
                setAction({ ...matrixAction, rules });
              }}
              onDelete={() => setAction({
                ...matrixAction,
                rules: matrixAction.rules.filter((_, idx) => idx !== i),
              })}
            />
          ))}
          <button
            type="button"
            className="af-grid-editor__add-btn"
            onClick={() => setAction({
              ...matrixAction,
              rules: [...matrixAction.rules, { condition: 'true', action: { kind: 'form', formId: 0, target: 'modal' } }],
            })}
          >
            + Add rule
          </button>
          <div className="af-grid-editor__matrix-fallback">
            <span className="af-property-editor__label" style={{ display: 'block', marginBottom: 4, marginTop: 10 }}>
              Fallback (no rule matches)
            </span>
            <LeafActionEditor
              action={matrixAction.fallback}
              onChange={(fa) => setAction({ ...matrixAction, fallback: fa })}
            />
          </div>
        </>
      )}
    </div>
  );
};

// ─── Button row editor ────────────────────────────────────────────────────────

const BTN_ACTION_OPTIONS = [
  { value: 'add', label: 'Add row' },
  { value: 'deleteSelected', label: 'Delete selected' },
  { value: 'editSelected', label: 'Edit selected' },
  { value: 'addQuickItems', label: 'Add quick items' },
];

interface ButtonRowProps {
  btn: GridButton;
  index: number;
  onUpdate: (i: number, b: GridButton) => void;
  onDelete: (i: number) => void;
}

const ButtonRow: React.FC<ButtonRowProps> = ({ btn, index, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const set = <K extends keyof GridButton>(key: K, val: GridButton[K]) =>
    onUpdate(index, { ...btn, [key]: val });

  return (
    <div className="af-grid-editor__btn-row">
      <div className="af-grid-editor__col-summary">
        <button
          type="button"
          className="af-grid-editor__col-expand"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? '▾' : '▸'} {btn.label || `Button ${index + 1}`}
          <span className="af-grid-editor__col-type-badge">{btn.buttonAction}</span>
        </button>
        <button type="button" onClick={() => onDelete(index)} className="af-grid-editor__col-delete" title="Delete button">✕</button>
      </div>

      {expanded && (
        <div className="af-grid-editor__col-detail">
          <TextPropertyEditor label="Label" value={btn.label} onChange={(v) => set('label', v)} />
          <SelectPropertyEditor
            label="Action"
            value={btn.buttonAction}
            onChange={(v) => set('buttonAction', v as ButtonAction)}
            options={BTN_ACTION_OPTIONS}
          />
          <TextPropertyEditor label="Emoji" value={btn.emoji ?? ''} onChange={(v) => set('emoji', v)} helpText="Emoji shown before label, e.g. ✓✗ (takes priority over Icon)" />
          <TextPropertyEditor label="Icon name" value={btn.icon ?? ''} onChange={(v) => set('icon', v)} helpText="CSS icon class, e.g. plus, trash, pencil (used when no emoji)" />
          <CheckboxPropertyEditor label="Requires selection" value={btn.requiresSelection ?? false} onChange={(v) => set('requiresSelection', v)} />
          {btn.requiresSelection && (
            <NumberPropertyEditor
              label="Selection limit"
              value={btn.selectionLimit ?? 0}
              onChange={(v) => set('selectionLimit', v || undefined)}
              helpText="0 = any number; 1 = exactly one"
              min={0} max={999}
            />
          )}
          {btn.buttonAction === 'addQuickItems' && (
            <JSONPropertyEditor
              label="Items (JSON)"
              value={JSON.stringify(btn.items ?? [], null, 2)}
              onChange={(v) => {
                try { set('items', JSON.parse(v)); } catch { /* keep previous */ }
              }}
              rows={8}
              helpText="Array of row objects to insert"
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main editor component ────────────────────────────────────────────────────

const GridEditorComponent: React.FC<PropertyEditorProps> = ({ control, onConfigChange }) => {
  const config = useMemo(() => (control.config as GridConfig) ?? {}, [control.config]);

  const set = useCallback(
    <K extends keyof GridConfig>(key: K, val: GridConfig[K]) => onConfigChange(key, val),
    [onConfigChange],
  );

  // ── Columns ──────────────────────────────────────────────────────────────

  const [columnsMode, setColumnsMode] = useState<'visual' | 'json'>('visual');
  const columns: GridColumn[] = config.columns ?? [];

  const updateColumn = useCallback(
    (i: number, updated: GridColumn) => {
      const next = [...columns];
      next[i] = updated;
      set('columns', next);
    },
    [columns, set],
  );

  const deleteColumn = useCallback(
    (i: number) => {
      const next = columns.filter((_, idx) => idx !== i);
      set('columns', next);
    },
    [columns, set],
  );

  const moveColumn = useCallback(
    (i: number, dir: 1 | -1) => {
      const next = [...columns];
      const [item] = next.splice(i, 1);
      next.splice(i + dir, 0, item);
      set('columns', next);
    },
    [columns, set],
  );

  const addColumn = useCallback(() => {
    set('columns', [
      ...columns,
      { key: `col${columns.length + 1}`, label: `Column ${columns.length + 1}`, type: 'text' },
    ]);
  }, [columns, set]);

  // ── View mode / card template (Task 19) ─────────────────────────────────

  const viewMode: GridViewMode = config.viewMode ?? 'grid';
  const cardTemplate: CardTemplateConfig = useMemo(
    () => config.cardTemplate ?? { titleField: '' },
    [config.cardTemplate],
  );

  const setCardTemplateField = useCallback(
    <K extends keyof CardTemplateConfig>(key: K, val: CardTemplateConfig[K]) =>
      set('cardTemplate', { ...cardTemplate, [key]: val }),
    [cardTemplate, set],
  );

  // Data-field picker options, sourced from this grid's own known columns (matches how every
  // other field-picker in this editor already works, e.g. FormLookupSelect for editFormID) —
  // excludes selector/link columns, which aren't real display data. Falls back to `c.id` when
  // `c.key` is absent — real-world schema JSON (e.g. FormID 30500's results-grid) authors
  // columns with "id", not "key"; GridControl.tsx normalises this at render time, but this
  // editor reads the raw, un-normalised config, so it must do the same fallback itself or every
  // pre-existing column silently produces zero picker options.
  const dataFieldOptions = useMemo(
    () =>
      columns
        .map((c) => ({ ...c, key: c.key || c.id || '' }))
        .filter((c) => c.type !== 'selector' && c.type !== 'link' && c.key)
        .map((c) => ({ value: c.key, label: c.label ? `${c.label} (${c.key})` : c.key })),
    [columns],
  );

  const toggleMetaField = useCallback(
    (key: string, on: boolean) => {
      const current = cardTemplate.metaFields ?? [];
      setCardTemplateField('metaFields', on ? [...current, key] : current.filter((k) => k !== key));
    },
    [cardTemplate.metaFields, setCardTemplateField],
  );

  // ── Buttons ──────────────────────────────────────────────────────────────

  const buttons: GridButton[] = config.buttons ?? [];

  const updateButton = useCallback(
    (i: number, updated: GridButton) => {
      const next = [...buttons];
      next[i] = updated;
      set('buttons', next);
    },
    [buttons, set],
  );

  const deleteButton = useCallback(
    (i: number) => set('buttons', buttons.filter((_, idx) => idx !== i)),
    [buttons, set],
  );

  const addButton = useCallback(
    () =>
      set('buttons', [
        ...buttons,
        { label: 'Add row', buttonAction: 'add' },
      ]),
    [buttons, set],
  );

  // ── Render ───────────────────────────────────────────────────────────────

  const features = config.features ?? {};

  const toggleExportFormat = (fmt: string, on: boolean) => {
    const current = features.exportFormats ?? [];
    set('features', {
      ...features,
      exportFormats: on ? [...current, fmt] : current.filter((f) => f !== fmt),
    });
  };

  const toggleImportFormat = (fmt: string, on: boolean) => {
    const current = features.importFormats ?? [];
    set('features', {
      ...features,
      importFormats: on ? [...current, fmt] : current.filter((f) => f !== fmt),
    });
  };

  return (
    <div className="af-grid-editor">
      {/* 1. Grid Setup */}
      <Section title="1. Grid Setup">
        <TextPropertyEditor label="Title" value={config.title ?? ''} onChange={(v) => set('title', v)} helpText="ARIA label for the grid table" />
        <SelectPropertyEditor
          label="Grid type"
          value={config.gridType ?? 'table'}
          onChange={(v) => set('gridType', v as GridType)}
          options={[
            { value: 'table', label: 'Table (default)' },
            { value: 'card', label: 'Card (V2)' },
            { value: 'list', label: 'List (V2)' },
          ]}
          helpText="Only 'table' is rendered in V1; others fall back silently. Unrelated to 'View mode' below, which is the real, live-rendered card/gallery feature."
        />
        <SelectPropertyEditor
          label="View mode"
          value={viewMode}
          onChange={(v) => set('viewMode', (v === 'grid' ? undefined : v) as GridConfig['viewMode'])}
          options={[
            { value: 'grid', label: 'Grid (default — data table)' },
            { value: 'cards', label: 'Cards' },
            { value: 'gallery', label: 'Gallery (image-forward)' },
          ]}
          helpText="How each row renders. Reuses this grid's own data-fetch/pagination/row-action config unchanged — only the per-row markup differs. 'Cards'/'Gallery' need a Card Template (below) to know which fields to show."
        />
        <TextPropertyEditor label="ID field" value={config.idField ?? ''} onChange={(v) => set('idField', v)} helpText="Row field used as stable identity — never serialised to output" />
        <TextPropertyEditor label="Empty message" value={config.emptyMessage ?? ''} onChange={(v) => set('emptyMessage', v)} helpText="Text shown when the grid has no rows" />
        <FormLookupSelect
          label="Edit Form"
          value={config.editFormID ?? null}
          onChange={(v) => set('editFormID', v !== null ? Number(v) : null)}
          placeholder="Built-in modal (none selected)"
        />
      </Section>

      {/* 1b. Card / Gallery Template — only meaningful once View mode above is 'cards'/'gallery' */}
      {viewMode !== 'grid' && (
        <Section title="1b. Card / Gallery Template">
          <p className="af-property-editor__help" style={{ marginBottom: 6 }}>
            Maps this grid's data fields onto the visual roles a card/gallery item needs. Pickers
            are populated from the columns defined in section 3 below.
          </p>
          <SelectPropertyEditor
            label="Title field (required)"
            value={cardTemplate.titleField || ''}
            onChange={(v) => setCardTemplateField('titleField', v)}
            options={[{ value: '', label: '— Select a field —' }, ...dataFieldOptions]}
            helpText="Rendered as the card's heading."
          />
          <SelectPropertyEditor
            label="Image field"
            value={cardTemplate.imageField ?? ''}
            onChange={(v) => setCardTemplateField('imageField', v || undefined)}
            options={[{ value: '', label: '— None —' }, ...dataFieldOptions]}
            helpText="Field holding an image URL — rendered as an <img>. Prominent/large in Gallery mode, smaller in Cards mode."
          />
          <SelectPropertyEditor
            label="Subtitle field"
            value={cardTemplate.subtitleField ?? ''}
            onChange={(v) => setCardTemplateField('subtitleField', v || undefined)}
            options={[{ value: '', label: '— None —' }, ...dataFieldOptions]}
            helpText="Short secondary line shown under the title."
          />
          <SelectPropertyEditor
            label="Description field"
            value={cardTemplate.descriptionField ?? ''}
            onChange={(v) => setCardTemplateField('descriptionField', v || undefined)}
            options={[{ value: '', label: '— None —' }, ...dataFieldOptions]}
            helpText="Longer body-copy field (e.g. a summary), clamped to a few lines."
          />
          <div className="af-property-editor__group">
            <span className="af-property-editor__label">Meta fields (shown as small chips)</span>
            {dataFieldOptions.length === 0 && (
              <p className="af-property-editor__help">Add columns in section 3 below to pick meta fields.</p>
            )}
            {dataFieldOptions.map((opt) => (
              <CheckboxPropertyEditor
                key={opt.value}
                label={opt.label}
                value={(cardTemplate.metaFields ?? []).includes(opt.value)}
                onChange={(v) => toggleMetaField(opt.value, v)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* 2. CSS */}
      <Section title="2. CSS &amp; Appearance">
        <TextPropertyEditor label="Table CSS class (tableClass)" value={config.tableClass ?? ''} onChange={(v) => set('tableClass', v)} helpText="CSS class applied to the outer grid wrapper" />
        <TextPropertyEditor label="Alternate row CSS class (altRowClass)" value={config.altRowClass ?? ''} onChange={(v) => set('altRowClass', v)} helpText="CSS class applied to even-index rows" />
        <CheckboxPropertyEditor
          label="Bulk edit enabled"
          value={config.bulkEditEnabled ?? false}
          onChange={(v) => set('bulkEditEnabled', v)}
          helpText="When on, all data cells are editable inline"
        />
      </Section>

      {/* 3. Columns */}
      <Section title="3. Columns">
        <div className="af-grid-editor__tab-bar">
          <button
            type="button"
            className={`af-grid-editor__tab${columnsMode === 'visual' ? ' af-grid-editor__tab--active' : ''}`}
            onClick={() => setColumnsMode('visual')}
          >
            Visual
          </button>
          <button
            type="button"
            className={`af-grid-editor__tab${columnsMode === 'json' ? ' af-grid-editor__tab--active' : ''}`}
            onClick={() => setColumnsMode('json')}
          >
            JSON
          </button>
        </div>

        {columnsMode === 'visual' ? (
          <>
            {columns.map((col, i) => (
              <ColumnRow
                key={col.key || `__col_${i}`}
                col={col}
                index={i}
                onUpdate={updateColumn}
                onDelete={deleteColumn}
                onMoveUp={(idx) => moveColumn(idx, -1)}
                onMoveDown={(idx) => moveColumn(idx, 1)}
                isFirst={i === 0}
                isLast={i === columns.length - 1}
              />
            ))}
            <button
              type="button"
              className="af-grid-editor__add-btn"
              onClick={addColumn}
            >
              + Add column
            </button>
          </>
        ) : (
          <JSONPropertyEditor
            label="Columns (JSON)"
            value={JSON.stringify(columns, null, 2)}
            onChange={(v) => {
              try { set('columns', JSON.parse(v)); } catch { /* keep previous */ }
            }}
            rows={20}
            helpText="Full column definition array"
          />
        )}
      </Section>

      {/* 4. New Entity Template */}
      <Section title="4. New Entity Template">
        <JSONPropertyEditor
          label="Template (JSON)"
          value={JSON.stringify(config.newEntityTemplate ?? {}, null, 2)}
          onChange={(v) => {
            try { set('newEntityTemplate', JSON.parse(v)); } catch { /* keep previous */ }
          }}
          rows={8}
          helpText="Default field values for a new blank row"
        />
      </Section>

      {/* 5. Buttons */}
      <Section title="5. Buttons">
        {buttons.map((btn, i) => (
          <ButtonRow key={`${btn.buttonAction}-${btn.label}-${i}`} btn={btn} index={i} onUpdate={updateButton} onDelete={deleteButton} />
        ))}
        <button type="button" className="af-grid-editor__add-btn" onClick={addButton}>
          + Add button
        </button>
      </Section>

      {/* 6. Features */}
      <Section title="6. Features">
        <CheckboxPropertyEditor
          label="Row reordering (drag-and-drop)"
          value={features.reorder ?? false}
          onChange={(v) => set('features', { ...features, reorder: v })}
        />
        <CheckboxPropertyEditor
          label="Pagination"
          value={features.pagination ?? false}
          onChange={(v) => set('features', { ...features, pagination: v })}
        />
        {features.pagination && (
          <NumberPropertyEditor
            label="Page size"
            value={config.pageSize ?? 10}
            onChange={(v) => set('pageSize', v)}
            min={1}
            max={200}
          />
        )}
        <div className="af-property-editor__group">
          <span className="af-property-editor__label">Export formats</span>
          <CheckboxPropertyEditor
            label="JSON"
            value={(features.exportFormats ?? []).includes('json')}
            onChange={(v) => toggleExportFormat('json', v)}
          />
          <CheckboxPropertyEditor
            label="CSV"
            value={(features.exportFormats ?? []).includes('csv')}
            onChange={(v) => toggleExportFormat('csv', v)}
          />
        </div>
        <div className="af-property-editor__group">
          <span className="af-property-editor__label">Import formats</span>
          <CheckboxPropertyEditor
            label="JSON"
            value={(features.importFormats ?? []).includes('json')}
            onChange={(v) => toggleImportFormat('json', v)}
          />
          <CheckboxPropertyEditor
            label="CSV"
            value={(features.importFormats ?? []).includes('csv')}
            onChange={(v) => toggleImportFormat('csv', v)}
          />
        </div>
      </Section>
    </div>
  );
};

GridEditorComponent.displayName = 'GridEditorComponent';

export const GridEditor: PropertyEditor = {
  type: 'grid',
  label: 'Grid',
  description: 'Configure the interactive data grid control',
  version: '1.0.0',
  render: (props) => <GridEditorComponent {...props} />,
};

export default GridEditor;
